import { useRef, useEffect } from 'react';

// `iframe` is a Tab stop in every browser: without it a dialog whose only other control is
// the close button traps keyboard users on that button and never lets them into the frame.
const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

function isFocusable(el: HTMLElement): boolean {
    return el.matches(FOCUSABLE_SELECTOR) && !el.closest('[inert]');
}

function getFocusableDescendants(container: HTMLElement | null): HTMLElement[] {
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.closest('[inert]'),
    );
}

// A tabindex="-1" target (a heading) is out of the Tab order but is still a valid landing
// spot for script-driven focus, so it is honoured directly rather than searched inside.
function resolveFocusTarget(el: HTMLElement | null | undefined): HTMLElement | null {
    if (!el) return null;
    if (isFocusable(el) || (el.hasAttribute('tabindex') && !el.closest('[inert]'))) return el;
    return getFocusableDescendants(el)[0] ?? null;
}

// Background claims are keyed per element and shared across instances so stacked dialogs
// may close in any order; the background is released only when the last claim goes.
const inertOwners = new WeakMap<HTMLElement, number>();
let scrollLockOwners = 0;

// Every currently-open overlay, so Escape can be routed to the topmost dialog alone.
const openOverlays = new Set<HTMLElement>();

// Topmost = no other open overlay follows it in document order (later sibling or nested).
function isTopmostOverlay(overlay: HTMLElement | null): boolean {
    if (!overlay) return true;
    for (const other of openOverlays) {
        if (other === overlay || !other.isConnected) continue;
        if (overlay.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING) return false;
    }
    return true;
}

// Claims inert on element siblings from the overlay up to document.body, returning the
// claims so release can drop exactly those.
function applyInertOutside(overlay: HTMLElement | null): HTMLElement[] {
    if (!overlay) return [];
    const claimed: HTMLElement[] = [];
    let node: HTMLElement = overlay;
    while (node !== document.body) {
        const parent = node.parentElement;
        if (!parent) break;
        for (const sibling of Array.from(parent.children)) {
            if (sibling === node || !(sibling instanceof HTMLElement)) continue;
            if (sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;
            const owners = inertOwners.get(sibling) ?? 0;
            // Inert no Modal set belongs to the page; never claim or later clear it.
            if (owners === 0 && sibling.hasAttribute('inert')) continue;
            inertOwners.set(sibling, owners + 1);
            sibling.setAttribute('inert', '');
            claimed.push(sibling);
        }
        node = parent;
    }
    return claimed;
}

function releaseInertOutside(claimed: HTMLElement[]): void {
    for (const el of claimed) {
        const owners = (inertOwners.get(el) ?? 1) - 1;
        if (owners > 0) {
            inertOwners.set(el, owners);
            continue;
        }
        inertOwners.delete(el);
        el.removeAttribute('inert');
    }
}

// Owns the dialog behaviour behind Modal's role="dialog" aria-modal="true" contract:
// focus landing, Tab trap, background inerting, scroll lock, topmost-only Escape, restore.
export function useModalDialog(
    isOpen: boolean,
    onClose: () => void,
    initialFocusRef?: React.RefObject<HTMLElement | null>,
) {
    const overlayRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const childrenRef = useRef<HTMLDivElement>(null);
    const closeButtonWrapperRef = useRef<HTMLSpanElement>(null);

    // Registers this overlay while open so every instance can see the rest of the stack.
    useEffect(() => {
        const overlay = overlayRef.current;
        if (!isOpen || !overlay) return;
        openOverlays.add(overlay);

        return () => {
            openOverlays.delete(overlay);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            // Every open dialog listens on document; only the topmost may close.
            if (event.key === 'Escape' && isTopmostOverlay(overlayRef.current)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) {
            if (scrollLockOwners === 0) document.body.style.overflow = 'unset';
            return;
        }
        scrollLockOwners += 1;
        document.body.style.overflow = 'hidden';

        return () => {
            scrollLockOwners = Math.max(0, scrollLockOwners - 1);
            if (scrollLockOwners === 0) document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Single effect owning open focus, background inerting, and close restore, so their
    // ordering is one code path. Landing priority: initialFocusRef, first focusable
    // descendant of children, close button, content container (tabIndex={-1}).
    useEffect(() => {
        if (!isOpen) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const claimed = applyInertOutside(overlayRef.current);

        const target =
            resolveFocusTarget(initialFocusRef?.current) ??
            getFocusableDescendants(childrenRef.current)[0] ??
            resolveFocusTarget(closeButtonWrapperRef.current) ??
            modalRef.current;
        target?.focus();

        return () => {
            releaseInertOutside(claimed);
            if (opener?.isConnected) opener.focus();
        };
    }, [isOpen, initialFocusRef]);

    // Tab trap: cycles within the overlay's focusable descendants, and pulls focus back in
    // when it is outside the overlay's DOM subtree (Tab inside a portalled Select bubbles
    // here via the React tree, not the DOM tree).
    const handleOverlayKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab') return;
        const focusables = getFocusableDescendants(overlayRef.current);
        if (focusables.length === 0) {
            event.preventDefault();
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const isInside = active instanceof HTMLElement && overlayRef.current?.contains(active);

        if (!isInside) {
            event.preventDefault();
            first.focus();
            return;
        }
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return { overlayRef, modalRef, childrenRef, closeButtonWrapperRef, handleOverlayKeyDown };
}
